<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRoute } from 'vue-router'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import MobileNavigation from '@/components/ui/MobileNavigation.vue'

const authStore = useAuthStore()
const route = useRoute()
const isAppMounted = ref(false)

// Páginas donde no mostrar navegación móvil
const hideNavOnPages = ['/login', '/register']
const showMobileNav = computed(() => {
  // Solo mostrar si la app está montada Y el usuario está autenticado
  if (!isAppMounted.value || !authStore.isAuthenticated) {
    return false
  }

  // No mostrar en páginas específicas
  return !hideNavOnPages.some(page => route.path.startsWith(page))
})

// Rutas app-like (chat): el wrapper mide exactamente el viewport y el padding
// del nav queda DENTRO (border-box), de modo que el documento nunca scrollea.
const isAppShell = computed(() => route.meta.appShell === true)

onMounted(() => {
  // La sesión ya la restauró el guard del router en la navegación inicial; esto solo reusa esa
  // misma promesa (no relee el storage ni repite la carga de la familia).
  void authStore.ensureSessionRestored()
  // Marcar la app como montada
  isAppMounted.value = true
})
</script>

<template>
  <div id="app" class="min-h-screen">
    <!-- Salto directo al contenido para usuarios de teclado/lector -->
    <a href="#main" class="skip-link">Saltar al contenido</a>

    <!-- Router view para mostrar las vistas -->
    <main
      id="main"
      :class="[{ 'mh-with-nav': showMobileNav }, isAppShell ? 'h-dvh overflow-hidden flex flex-col' : '']"
    >
      <router-view />
    </main>

    <!-- Navegación inferior - cuando el usuario esté autenticado (mobile-first, disponible también en web) -->
    <Transition name="slide-up" mode="out-in">
      <MobileNavigation v-if="showMobileNav" key="mobile-nav" />
    </Transition>

    <!-- Sistema de notificaciones toast - solo después de que la app esté montada -->
    <ToastContainer v-if="isAppMounted" />

    <!-- Sistema de confirmaciones - solo después de que la app esté montada -->
    <ConfirmDialog v-if="isAppMounted" />
  </div>
</template>

<style>
/* Los estilos globales están en style.css */

/* Enlace de salto: invisible hasta recibir foco con teclado */
.skip-link {
  position: absolute;
  left: -9999px;
  z-index: 100;
  padding: 10px 16px;
  background: var(--navy);
  color: var(--white);
  border-radius: var(--r-sm);
  font-weight: 600;
  text-decoration: none;
}
.skip-link:focus {
  left: 12px;
  top: 12px;
}

/* Transiciones para navegación móvil */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
